/**
 * ActionGroup — collapses a run of Asthra "thinking" tool/retrieval steps into
 * ONE dropdown instead of a stack of individual boxes in the chat thread.
 *
 * Behavior (per product decision): while a run is working, the header shows a
 * live count ("Looking things up… 3 steps"); once the steps are persisted
 * (done), it renders collapsed by default as "▸ N actions" that the user can
 * expand to see each step as a plain row (no boxes).
 *
 * Pairs with StepRow, the compact single-line renderer for one step.
 */

import { makeStyles, tokens } from "@fluentui/react-components";
import * as React from "react";

const useStyles = makeStyles({
  group: {
    margin: "3px 0",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "8px",
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    width: "100%",
    padding: "5px 10px",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    font: "inherit",
    color: tokens.colorNeutralForeground2,
    textAlign: "left",
  },
  chevron: {
    fontSize: "9px",
    width: "10px",
    flexShrink: 0,
    color: tokens.colorNeutralForeground3,
  },
  title: { fontSize: "12px", fontWeight: 600, flexGrow: 1 },
  count: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground4,
    borderRadius: "8px",
    padding: "0 6px",
    flexShrink: 0,
  },
  liveDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    flexShrink: 0,
    backgroundColor: tokens.colorBrandBackground,
    animationName: {
      "0%, 100%": { opacity: 0.3 },
      "50%": { opacity: 1 },
    },
    animationDuration: "1.1s",
    animationIterationCount: "infinite",
  },
  body: {
    padding: "1px 8px 6px 8px",
    display: "flex",
    flexDirection: "column",
    gap: "1px",
  },
  // ── StepRow ──
  row: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    padding: "2px 0",
    fontSize: "11.5px",
    color: tokens.colorNeutralForeground2,
    minWidth: 0,
  },
  dot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    flexShrink: 0,
    alignSelf: "center",
    backgroundColor: tokens.colorNeutralForeground4,
  },
  dotRunning: { backgroundColor: tokens.colorBrandBackground },
  dotDone: { backgroundColor: tokens.colorPaletteGreenForeground1 },
  dotError: { backgroundColor: tokens.colorPaletteRedForeground1 },
  name: { fontWeight: 600, flexShrink: 0 },
  summary: {
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  meta: { color: tokens.colorNeutralForeground4, flexShrink: 0, marginLeft: "auto" },
});

export interface StepRowProps {
  name: string;
  summary?: string;
  meta?: string;
  status?: "running" | "done" | "error";
}

/** One tool/retrieval step rendered as a compact single line (no box). */
export const StepRow: React.FC<StepRowProps> = ({ name, summary, meta, status }) => {
  const styles = useStyles();
  const dotClass =
    status === "running"
      ? styles.dotRunning
      : status === "error"
        ? styles.dotError
        : status === "done"
          ? styles.dotDone
          : "";
  return (
    <div className={styles.row}>
      <span className={`${styles.dot} ${dotClass}`} aria-hidden />
      <span className={styles.name}>{name}</span>
      {summary ? <span className={styles.summary}>{summary}</span> : null}
      {meta ? <span className={styles.meta}>{meta}</span> : null}
    </div>
  );
};

export interface ActionGroupProps {
  count: number;
  /** True while the run is still producing steps — shows the live label. */
  live?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}

export const ActionGroup: React.FC<ActionGroupProps> = ({ count, live = false, defaultOpen = false, children }) => {
  const styles = useStyles();
  const [open, setOpen] = React.useState(defaultOpen);
  const plural = count === 1 ? "" : "s";
  const label = live ? `Looking things up… ${count} step${plural}` : `${count} action${plural}`;
  return (
    <div className={styles.group}>
      <button
        type="button"
        className={styles.header}
        aria-expanded={open}
        aria-label={label}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.chevron} aria-hidden>
          {open ? "▾" : "▸"}
        </span>
        {live ? <span className={styles.liveDot} aria-hidden /> : null}
        <span className={styles.title}>{label}</span>
        {!live ? <span className={styles.count}>{count}</span> : null}
      </button>
      {open ? <div className={styles.body}>{children}</div> : null}
    </div>
  );
};
